import { useEffect, useState } from 'react'
import { Tabs } from '@/components/Common/Tabs'
import Layout from '@/components/Layout/Layout'
import AccountTab from '@/components/Settings/AccountTab'
import MinutesTab, { GET_MY_MINUTES, type MyMinutesOverview } from '@/components/Settings/MinutesTab'
import PreferencesTab from '@/components/Settings/PreferencesTab'
import { User, Clock, Settings as SettingsIcon } from 'lucide-react'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { useTranslation } from '@/hooks'

function SettingsPage() {
  const { t } = useTranslation()

  // The old billing tab showed an invented subscription to everybody. It is replaced
  // by a Minutes tab, and only a user who actually has metered modules sees one: the
  // server answers `metered: false` for everyone else.
  const [minutes, setMinutes] = useState<MyMinutesOverview | null>(null)

  useEffect(() => {
    let cancelled = false
    authorizedGraphQLRequest<{ getMyMinutesOverview: MyMinutesOverview }>(GET_MY_MINUTES)
      .then((response) => {
        if (!cancelled) setMinutes(response.getMyMinutesOverview)
      })
      .catch((error) => console.error('Failed to load your minutes:', error))
    return () => {
      cancelled = true
    }
  }, [])

  const tabs = [
    {
      value: 'account',
      label: t('settings.tabs.account'),
      content: <AccountTab />,
      icon: <User size={16} />,
    },
    ...(minutes?.metered
      ? [
          {
            value: 'minutes',
            label: t('settings.tabs.minutes'),
            content: <MinutesTab overview={minutes} />,
            icon: <Clock size={16} />,
          },
        ]
      : []),
    {
      value: 'settings',
      label: t('settings.tabs.preferences'),
      content: <PreferencesTab />,
      icon: <SettingsIcon size={16} />,
    },
  ]

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 pb-8">
        <div className="container mx-auto p-4 max-w-3xl">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border-0 p-8 hover:shadow-2xl transition-all duration-300">
            <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-primary to-primary/70 rounded-xl shadow-md">
                  <SettingsIcon className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">{t('settings.title')}</h2>
              </div>
            </div>
            <Tabs tabs={tabs} defaultValue="account" className="w-full" />
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default SettingsPage
